/**
 * Design tokens for the pre-send review popup.
 *
 * Sizes, spacing and type are the Figma "Pre-send review popup" values (file `InkFree web application`,
 * node 9012-2849, read through the Figma MCP). Colours are deliberately NOT the mock's: every one is an
 * existing `theme/colors` token, and the shell is `AppModal` with its own chrome (title, close, footer,
 * tones), so the popup reads like every other modal in the app. Where the mock's colour is already a
 * token it is that token (`error.dark`, `signer.border.light`, `signer.surface.muted`, `signer.text.*`).
 */
import {
  contentCard,
  error,
  inkfree,
  notification,
  signer,
  statusChip,
} from "theme/colors";
import appModalTokens from "global/components/AppModal/AppModal.tokens";

/** The first value of an `AppModal` padding shorthand: the body's top padding. */
const bodyTopPadding = (padding: string) => padding.split(" ")[0];

const presendReviewTokens = {
  modal: {
    /** The design's popup width; `AppModal` takes it through `width`. */
    width: 600,
  },

  color: {
    ink: signer.ink,
    body: signer.text.secondary,
    sub: signer.text.meta,
    muted: signer.text.faint,
    border: signer.border.light,
    surface: signer.surface.muted,
    card: contentCard.bg,
    link: inkfree.main,
    /** The app's row hover wash, under a document header that opens and closes. */
    hover: signer.surface.hover,
  },

  /** "PRE-SEND REVIEW" over the title. */
  eyebrow: {
    fontSize: "10.5px",
    fontWeight: 700,
    letterSpacing: "0.63px",
    gap: "6px",
  },

  /** The count chips under the summary ("• 1 High"). */
  countChip: {
    rowGap: "6px",
    rowPaddingTop: "6px",
    padding: "4px 10px 4px 9px",
    gap: "6px",
    radius: "999px",
    fontSize: "12px",
    fontWeight: 600,
    dotSize: 6,
    iconSize: 12,
  },

  body: {
    /** Between the parts of one document: header, headings, cards, notices. */
    documentGap: "12px",
    /** Between two documents, either side of the divider. */
    betweenDocuments: "14px",
  },

  document: {
    gap: "12px",
    tilePadding: "8px",
    tileRadius: "8px",
    iconSize: 18,
    nameSize: "14px",
    nameWeight: 600,
    nameLineHeight: "20px",
    metaSize: "12px",
    metaLineHeight: "16px",
    checkingSize: "12px",
    checkingWeight: 600,
    /** Phones: the status pill drops under the name, this far below it. */
    phoneRowGap: "6px",
  },

  /**
   * A document header that opens and closes its findings, when the review covers several documents. Not
   * in the Figma (it shows one document): added so a multi-document review is not one long scroll.
   */
  disclosure: {
    chevronSize: 16,
    /** The hover wash and focus ring reach this far (px) past the header on each side. */
    bleedX: 8,
    bleedY: 6,
    radius: "8px",
    duration: "200ms",
    /**
     * The header stays pinned at the top of the scroll area while its findings scroll under it. A sticky
     * box pins inside the scroller's padding, so the offset climbs back over the dialog body's top padding
     * (or findings show in the band above it), plus 1px past the edge: that clipped pixel is how the popup
     * tells the header is pinned (it then draws a hairline under it).
     */
    stickyTop: `calc(-${bodyTopPadding(appModalTokens.body.padding)} - 1px)`,
    sheetStickyTop: `calc(-${bodyTopPadding(appModalTokens.body.sheetPadding)} - 1px)`,
  },

  statusPill: {
    padding: "4px 10px 4px 9px",
    gap: "5px",
    radius: "999px",
    iconSize: 13,
    fontSize: "12px",
    fontWeight: 600,
    ready: { bg: statusChip.tone.success.bg, text: statusChip.tone.success.text },
    needs_attention: { bg: notification.warning.bg, text: notification.warning.text },
    not_reviewed: { bg: signer.surface.muted, text: signer.text.secondary },
  },

  categoryHeading: {
    height: "20px",
    gap: "8px",
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.66px",
  },

  card: {
    radius: "10px",
    accentWidth: "3px",
    /** The design's 16px inset, plus the 3px accent strip drawn inside the left edge. */
    padding: "14px 16px 16px 19px",
    gap: "8px",
    titleSize: "14px",
    titleWeight: 600,
    titleLineHeight: "20px",
    detailSize: "13px",
    detailLineHeight: "19px",
    whatToDoSize: "10.5px",
    whatToDoWeight: 700,
    whatToDoSpacing: "0.63px",
    whatToDoGap: "2px",
    suggestionSize: "13px",
    suggestionWeight: 600,
    suggestionLineHeight: "19px",
    actionSize: "12px",
    actionWeight: 600,
    dismissGap: "4px",
    dismissIconSize: 12,
    locationGap: "5px",
    locationIconSize: 13,
    snippet: {
      padding: "8px 12px",
      radius: "6px",
      fontSize: "12px",
      lineHeight: "18px",
    },
  },

  severityChip: {
    padding: "3px 9px 3px 8px",
    gap: "6px",
    radius: "999px",
    dotSize: 6,
    fontSize: "11px",
    fontWeight: 600,
  },

  /** Per severity: the card's accent bar, the chip's tint and its label/dot colour. */
  severity: {
    high: { accent: error.dark, bg: notification.error.bg, text: error.dark },
    medium: {
      accent: signer.warn.main,
      bg: notification.warning.bg,
      text: notification.warning.text,
    },
    low: { accent: signer.text.faint, bg: signer.surface.muted, text: signer.text.meta },
  },

  notice: {
    padding: "10px 12px",
    radius: "8px",
    gap: "10px",
    iconSize: 16,
    titleSize: "13px",
    titleWeight: 600,
    titleLineHeight: "19px",
    detailSize: "12px",
    detailLineHeight: "17px",
    textGap: "2px",
    infoGap: "8px",
    infoIconSize: 14,
  },

  skeleton: {
    padding: "16px",
    gap: "10px",
    radius: "5px",
    title: { width: 70, height: 16 },
    lineLong: { width: 440, height: 10 },
    lineShort: { width: 300, height: 10 },
  },

  footer: {
    noteSize: "12px",
    noteLineHeight: "17px",
  },

  spinner: {
    duration: "0.9s",
  },
} as const;

export type ReviewSeverityTone = keyof typeof presendReviewTokens.severity;
export type ReviewStatusTone = keyof Pick<
  typeof presendReviewTokens.statusPill,
  "ready" | "needs_attention" | "not_reviewed"
>;

export default presendReviewTokens;
